import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface DatabookRecord {
  id: string;
  contrato_id: string;
  boletim_id: string | null;
  status: string;
  progresso_percentual: number;
  titulo: string;
  documento_ids: string[];
  total_documentos: number;
  tamanho_bytes: number | null;
  hash_sha256: string | null;
  grupo_geracao: string;
  volume_numero: number;
  total_volumes: number;
  versao_numero: number;
  erros_parciais: any[];
  erro_fatal: string | null;
  storage_path: string | null;
  created_by: string;
  created_at: string;
  downloadUrl?: string;
}

export function useEmpDatabooks(contratoId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);

  const { data: databooks = [], isLoading, refetch } = useQuery({
    queryKey: ['emp-databooks', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_databooks')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as DatabookRecord[];
    },
    enabled: !!contratoId,
  });

  // Polling while any databook is processing
  const hasProcessing = databooks.some((d) => d.status === 'processing');

  useEffect(() => {
    if (!hasProcessing) return;
    const interval = setInterval(() => {
      refetch();
    }, 5000);
    return () => clearInterval(interval);
  }, [hasProcessing, refetch]);

  // Get signed download URL (secure backend first, then storage fallback)
  const getDownloadUrl = useCallback(async (storagePath: string): Promise<string | null> => {
    const { data: fnData, error: fnError } = await supabase.functions.invoke('arquivo-get-download-url', {
      body: {
        bucket: 'emp-databooks',
        storage_path: storagePath,
        expires_in: 3600,
      },
    });

    if (!fnError && fnData?.signed_url) {
      return fnData.signed_url as string;
    }

    const { data, error } = await supabase.storage
      .from('emp-databooks')
      .createSignedUrl(storagePath, 3600);

    if (error) {
      console.error('Download URL error:', fnError || error);
      return null;
    }

    return data?.signedUrl || null;
  }, []);

  // Trigger databook generation
  const gerarDatabook = useCallback(async (documentoIds: string[], titulo: string, boletimId?: string) => {
    setGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase.functions.invoke('gerar-databook', {
        body: {
          contrato_id: contratoId,
          documento_ids: documentoIds,
          titulo,
          boletim_id: boletimId || null,
        },
      });

      if (error) throw error;

      if (data?.error) {
        if (data.error === 'generation_in_progress') {
          toast({ variant: 'destructive', title: 'Geração em andamento', description: 'Já existe um Databook sendo gerado para este contrato.' });
        } else {
          throw new Error(data.error);
        }
        return null;
      }

      toast({ title: 'Databook iniciado', description: `Geração em andamento (${data.total_volumes} volume${data.total_volumes > 1 ? 's' : ''})` });
      queryClient.invalidateQueries({ queryKey: ['emp-databooks', contratoId] });
      return data;
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Erro ao gerar Databook', description: err.message });
      return null;
    } finally {
      setGenerating(false);
    }
  }, [contratoId, toast, queryClient]);

  return {
    databooks,
    isLoading,
    hasProcessing,
    generating,
    gerarDatabook,
    getDownloadUrl,
    refetch,
  };
}
